import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useLocation, useNavigate } from "@/lib/router-compat";

const StickyMobileCTA = () => {
  const [visible, setVisible] = useState(false);
  const [pricingOnScreen, setPricingOnScreen] = useState(false);
  const barRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";
  // The checkout page has its own sticky pay bar.
  const isCheckout = location.pathname.startsWith("/checkout");

  // Reserve page space equal to the bar's real height so it never covers
  // the footer or any content underneath it.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    if (!visible) {
      root.style.setProperty("--sticky-cta-h", "0px");
      return;
    }
    const apply = () => {
      const h = barRef.current?.offsetHeight ?? 0;
      root.style.setProperty("--sticky-cta-h", `${h}px`);
    };
    const raf = requestAnimationFrame(apply);
    window.addEventListener("resize", apply);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", apply);
      root.style.setProperty("--sticky-cta-h", "0px");
    };
  }, [visible]);


  useEffect(() => {
    if (isCheckout) {
      setVisible(false);
      return;
    }
    const handleScroll = () => {
      setVisible(window.scrollY > window.innerHeight * 0.8);
    };

    // On non-home pages show immediately after a small scroll
    if (!isHome) {
      const handleSmallScroll = () => {
        setVisible(window.scrollY > 100);
      };
      window.addEventListener("scroll", handleSmallScroll, { passive: true });
      handleSmallScroll();
      return () => window.removeEventListener("scroll", handleSmallScroll);
    }

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isHome, isCheckout]);

  // The pricing card already shows the same offer, so hide the bar there.
  useEffect(() => {
    if (!isHome || typeof IntersectionObserver === "undefined") {
      setPricingOnScreen(false);
      return;
    }
    const el = document.getElementById("pricing");
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => setPricingOnScreen(entries[0]?.isIntersecting ?? false),
      { threshold: 0.25 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isHome]);

  const handleClick = () => {
    if (isHome) {
      const el = document.getElementById("pricing");
      if (el) {
        const navHeight = 64;
        const stickyBarHeight = 80;
        const sectionHeight = el.scrollHeight;
        const viewportHeight = window.innerHeight;
        const availableSpace = viewportHeight - navHeight - stickyBarHeight;

        let top: number;
        if (sectionHeight <= availableSpace) {
          const offset = (availableSpace - sectionHeight) / 2;
          top =
            el.getBoundingClientRect().top +
            window.scrollY -
            navHeight -
            offset;
        } else {
          top =
            el.getBoundingClientRect().top +
            window.scrollY +
            sectionHeight -
            viewportHeight +
            stickyBarHeight;
        }

        window.scrollTo({ top, behavior: "smooth" });
      }
    } else {
      navigate("/checkout");
    }
  };

  return (
    <AnimatePresence>
      {visible && !isCheckout && !pricingOnScreen && (
        <motion.div
          ref={barRef}
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="mobile-floating fixed bottom-0 left-0 right-0 z-50 p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] bg-background/90 backdrop-blur-lg border-t border-border/50 md:hidden"
        >
          <Button
            onClick={handleClick}
            className="w-full bg-primary hover:bg-foreground hover:text-background text-primary-foreground transition-colors font-bold text-base py-5 rounded-none shadow-lg"
          >
            Get Your Trail Pass - Only $10
          </Button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default StickyMobileCTA;
